<template>
    <!-- Dashboard layout -->
    <div id="app-layout" v-show="displayApp">
        <CustomStyles></CustomStyles>   
        <div class="container">  
            <!-- Link will be visible only when user is using keyboard navigation (tab) or screen reader. On click it allows user to head directly to the content section -->
            <div class="row">
                <div class="col-lg-12 col-md-12 col-sm-12">
                    <vue-skip-to to="#main" text="Skip to main content" aria-label="Skip to main content" /> 
                </div>
            </div>

            <header>
                <div id="divHeader">
                    <Header></Header>
                    <div class="row">
                        <Navigation></Navigation>
                    </div>
                </div>
            </header>  

            <main id="main" role="main">
                <div class="row main-content-row top10">
                    <router-view />
                </div>
            </main>
        </div>

        <footer>
            <div id="footer">
                <Footer></Footer>            
            </div>
        </footer>             

        <!-- Session Timeout -->
        <modal v-show="isSessionTimeoutModalVisible" @close="closeModalAndSession(true)">
            <h4 class="modal-title" slot="header">{{ $t("message.sessionWarning") }}</h4>
            <div slot="body">
                <div class="col-lg-12 top10">
                    <label>{{ getTimeoutMessage }}</label>
                </div>
            </div>
            <div slot="footer">
                <button id="btnOk" class="btn btn-sm btn-primary" @click.prevent="closeModalAndSession(false)">{{ $t("message.ok") }}</button>
                <button id="btnCancel" class="btn btn-sm btn-primary" @click.prevent="closeModalAndSession(true)">{{ $t("message.cancel") }}</button>
            </div>
        </modal>

        <!-- Account Not Valid -->
        <modal v-show="accountNotValidModalVisible" @close="closeModalAndReload()">
            <h4 class="modal-title" slot="header">{{ $t("message.accountNotValid") }}</h4>
            <div slot="body">
                <div class="col-lg-12 top10">
                    <label v-html="accountNotValidMessage"></label>
                </div>
            </div>
            <div slot="footer">
                <button id="btnAccountNotValid" class="btn btn-sm btn-primary" @click.prevent="closeModalAndReload()">{{ $t("message.ok") }}</button>
            </div>
        </modal>
    </div>
</template>

<script>
import axios from "axios";
import CustomStyles from '@/components/common/CustomStyles';
import Header from '@/components/common/Header';
import Navigation from '@/components/common/Navigation';
import Footer from '@/components/common/Footer';
import Modal from '@/components/common/Modal';
import { Idle } from 'idlejs/dist';

export default {
    name: 'AppLayout',
    components: {
        CustomStyles,
        Header,
        Navigation,
        Footer,
        Modal
    },
    data() {
        return {
            isSessionTimeoutModalVisible: false,
            extraTimeout: 5,
            extraTimeoutIntervalId: '',
            accountNotValidModalVisible: false,
            accountNotValidIntervalId: '',
            accountNotValidMessage: '',
            billingSummaryDataIntervalId: '',
            sessionTimeoutValue: this.$store.getters.getSessionTimeout
        }
    },    
    mounted() {        
        this.$store.commit('setCurrentPageLayout', this.$LayoutType.AppLayout);

        // Subscribe to 'setCustomerInfo' mutation so we can keep an eye on customerInfo.RenderPagesInIframe property (Clients are using Payment page by SSO)
        // If true - Customer Portal pages will be displayed in an iframe - in that case we need to increase the session timeout to 25 minutes, and disable account valid checks
        // Once the session has timed out, instead of routing the user to Login page we will redirect them to a new component (SessionTimeout.vue) - this will display session timeout message same as Processing.
        // If user tries to navigate to Login page - they will not have access to be able to login.
        // If false - set Customer Portal session timeout (15 minutes) and enable account validity checks and Login page should display its normal content.
        this.$store.subscribe((mutation, state) => {
            switch(mutation.type) {
                case 'setCustomerInfo':
                    const pageInIframe = state.customerInfo.RenderPagesInIframe;

                    if (pageInIframe) {
                        // Resetting Session Timeout to 25 minutes
                        this.sessionTimeoutValue = this.$SessionTimeoutType.PortalInIframe;

                        // Clear Intervals - Session timeout
                        if (this.extraTimeoutIntervalId !== '') {
                            window.clearInterval(this.extraTimeoutIntervalId);
                        }

                        // Clear Interval - Account not valid
                        if (this.accountNotValidIntervalId !== '') {
                            window.clearInterval(this.accountNotValidIntervalId); 
                        }                      
                    } else {
                        this.sessionTimeoutValue = this.$SessionTimeoutType.Normal;

                        // Check for if the Customer we are currently viewing is valid or not
                        this.accountNotValidIntervalId = window.setInterval(() => { this.checkIfCustomerIsValid() }, 1000); 
                    }
                break;
            }
        });   

        this.fetchBillingSummaryData();

        this.$store.commit('setSessionTimeout', this.sessionTimeoutValue);

        // Show Idle session warning after 15/25 minutes (based on RenderPagesInIframe)
        const idle = new Idle().whenNotInteractive().within(this.sessionTimeoutValue).do(() => this.handleSessionTimeout()).start();
    },
    methods: {
        fetchBillingSummaryData() {
            axios({
                method: "GET",
                url: this.$httpAxiosBaseUrl() + "Home/GetSettingsForBillingSummary?date=" + new Date().getTime()
            }).then(result => {
                var response = result.data;
                if (response !== '') {
                    // Check if 'DisplayCustomerInfoBox' rep setting is set to true                  
                    const displayCustomerInformation = response.DisplayHeaderAccountInformation;
                    this.$store.commit('setDisplayHeaderAccountInformation', displayCustomerInformation);

                    // Check if client has access to Home navigation menu item
                    const userHasAccessToHomepage = response.IsCustomerHomeMenuEnabled;
                    this.$store.commit('setIsCustomerHomeMenuEnabled', userHasAccessToHomepage);

                    // If any of the above are true, we will go ahead and place the call to get billing summary and setup the interval as well
                    if (displayCustomerInformation || userHasAccessToHomepage) {
                        this.getBillingSummaryData();
                        // Refresh billing summary data based on our homepage timer
                        this.billingSummaryDataIntervalId = window.setInterval(() => { this.getBillingSummaryData(); }, this.$store.getters.getHomepageTimer);
                    }
                }
            }, error => {
                console.log(error);
            });
        },
        handleSessionTimeout() {
            this.$store.getters.getRenderPagesInIframe ? this.closeModalAndRedirectToNewPageForSessionTimeout() : this.showModal();
        },
        showModal() {
            this.isSessionTimeoutModalVisible = true;
            this.beginTimerCountdown();
        },
        beginTimerCountdown() {
            // Set up a timer for 5 minutes, once it's at zero close this session and redirect user back to login
            this.extraTimeoutIntervalId = window.setInterval(() => { this.decrementTimeout() }, 60000);
        },
        decrementTimeout() {
            if (this.extraTimeout <= 0) {
                this.closeModalAndSession(true);
            } else {
                this.extraTimeout--;
            }
        },
        closeModalAndSession(sendBackToLogin) {
            this.closeModalAndClearIntervals();           

            if (sendBackToLogin) {
                this.$router.push({ name: 'Login' });
            }
        },
        closeModalAndClearIntervals() {
            this.isSessionTimeoutModalVisible = false;
            this.clearAllWindowIntervals();
            
            this.extraTimeout = 5;
            this.sessionTimeoutValue = this.$SessionTimeoutType.Normal;
        },
        clearAllWindowIntervals() {
            window.clearInterval(this.extraTimeoutIntervalId);
            window.clearInterval(this.accountNotValidIntervalId);
            window.clearInterval(this.billingSummaryDataIntervalId);
            // Check if we have our interval id saved off in the store, if we do we will clear off that interval as well
            var spIntervalId = this.$store.getters.getServicePointIntervalId;
            if (spIntervalId !== '') {
                window.clearInterval(spIntervalId);
                this.$store.commit('setServicePointIntervalId', '');
            }
        },
        closeModalAndRedirectToNewPageForSessionTimeout() {
            this.closeModalAndClearIntervals();
            this.$router.push({ name: 'SessionTimeout' });
        },
        checkIfCustomerIsValid() {
            var currentCustomer = this.$store.getters.getAccountNumber;

            // Check if the customer is valid (no need to check if we already are displaying the 'Account Not Valid' modal)
            if (currentCustomer !== '' && !this.accountNotValidModalVisible) {
                axios({
                    method: "POST",
                    data: {
                        customerAccountNumber: currentCustomer
                    },
                    url: this.$httpAxiosBaseUrl() + "Home/CheckIfCustomerIsValid?date=" + new Date().getTime()
                }).then(result => {
                    var response = result.data;
                    if (response !== '') {
                        this.accountNotValidModalVisible = !response.IsValid;
                        this.accountNotValidMessage = response.DisplayMessage;
                    } else {
                        // Hide Account Not Valid modal and reset message details, we will go ahead and redirect the user back to Login page.
                        // Account not valid is checked for clients that are not being rendered in an iframe.
                        this.resetAccountNotValidModalDetails();
                        this.clearAllWindowIntervals();
                        this.$router.push({ name: 'Login' });
                    }
                }, error => {
                    console.log(error);
                });
            }
        },
        showAccountNotValidModal() {
            this.accountNotValidModalVisible = true;
        },
        closeModalAndReload() {
            window.clearInterval(this.accountNotValidIntervalId);
            this.resetAccountNotValidModalDetails();

            window.location.reload(true); 
        },
        resetAccountNotValidModalDetails() {
            this.accountNotValidModalVisible = false;
            this.accountNotValidMessage = '';
        },
        getBillingSummaryData() {
            axios({
                method: "GET",
                url: this.$httpAxiosBaseUrl() + "Home/GetBillingSummary?date=" + new Date().getTime()
            }).then(result => {
                var response = result.data;
                if (response !== '') {
                    this.$store.commit('setBillingSummaryData', response);
                }
            }, error => {
                console.log(error);
            });
        }
    },
    computed: {
        getTimeoutMessage() {
            var timeoutMessage = this.$store.getters.getTimeoutMessage;
            if (timeoutMessage !== undefined) { 
                return timeoutMessage.replace('{0}', this.extraTimeout);
            } else {
                return "";
            }            
        },
        displayApp() {
            var customerInfoLoaded = this.$store.getters.getCustomerInfoLoaded;
            var repThemeLoaded = this.$store.getters.getRepThemeLoaded;
            
            return customerInfoLoaded && repThemeLoaded;
        }
    }
}
</script>

<style scoped>
.vue-skip-to {
    width: 100%;
    background: #f1f1f1 !important;
    text-align: center;
}
</style>
